/**
 * Refuges et campings de l'organisation, en onglets : Refuges, Campings, Emplacements (de camping,
 * rangés sous leur camping parent), Hébergements (s'il y en a) et Tarification (grille de prix des
 * refuges et campings). Chaque lieu : statistiques, recherche, et les actions modifier, voir,
 * dupliquer, supprimer. Les POI sont dans Équipements & POI. L'onglet ouvert est dans l'adresse (?onglet=).
 */
import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { OrganizationSettings, type OrgSettingsTab } from '@web/features/organizations/components/OrganizationSettings'
import '@web/features/organizations/styles/OrganizationEditPage.css'
import { useOrganizationLocations } from '@web/features/organizations/hooks/useOrganizationLocations'
import {
  deleteOrganizationLocation,
  duplicateOrganizationLocation,
} from '@web/features/organizations/services/organizationApi'
import { fetchInsights } from '../api'
import { StatusPill, TypeIcon } from '../components/badges'
import { useOrg } from '../utils/orgContext'
import { formatDate, formatNumber } from '../utils/format'
import { publicLocationUrl } from '../utils/links'
import { EDITABLE_TYPES, editLocationPath } from '../utils/routes'
import { PitchesPanel } from '../components/PitchesPanel'

const TABS = ['refuges', 'campings', 'emplacements', 'hebergements', 'tarification'] as const
type Tab = (typeof TABS)[number]
/** Type de lieu de chaque onglet de liste. */
const TAB_TYPE: Partial<Record<Tab, string>> = { refuges: 'refuge', campings: 'camping', hebergements: 'accommodation' }
/** L'onglet Tarification réutilise l'éditeur de la fiche, réduit à la tarification. */
const ALL_BUT_PRICING: OrgSettingsTab[] = ['identity', 'location', 'access', 'facilities', 'media']

export function LieuxPage() {
  const { t } = useTranslation('gestion')
  const { orgId } = useOrg()
  const queryClient = useQueryClient()
  const [q, setQ] = useState('')
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [params, setParams] = useSearchParams()
  const tab: Tab = TABS.find((x) => x === params.get('onglet')) ?? 'refuges'
  const setTab = (next: Tab) => setParams(next === 'refuges' ? {} : { onglet: next }, { replace: true })
  const { data: allLocations = [], isLoading, error: loadError } = useOrganizationLocations(orgId)
  const locations = useMemo(() => allLocations.filter((l) => l.type !== 'poi'), [allLocations])
  const { data: insights } = useQuery({
    queryKey: ['insights', orgId, 30],
    queryFn: ({ signal }) => fetchInsights(orgId, 30, signal),
  })

  const views = useMemo(() => {
    const m = new Map<string, number>()
    for (const el of insights?.elements ?? []) m.set(`${el.type}-${el.id}`, el.views)
    return m
  }, [insights])

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: locations.length }
    for (const l of locations) c[l.type] = (c[l.type] ?? 0) + 1
    return c
  }, [locations])

  const listType = TAB_TYPE[tab]
  const visible = locations.filter((l) => l.type === listType && (!q.trim() || l.name.toLowerCase().includes(q.trim().toLowerCase())))
  // L'onglet Hébergements n'apparaît que s'il y en a.
  const tabs = TABS.filter((x) => x !== 'hebergements' || counts.accommodation)

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['organization', orgId, 'locations'] })
    void queryClient.invalidateQueries({ queryKey: ['insights', orgId] })
  }

  const run = async (key: string, action: () => Promise<unknown>) => {
    setBusyKey(key)
    setError(null)
    try {
      await action()
      refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusyKey(null)
    }
  }

  return (
    <div className="g-page">
      <div className="g-page__head">
        <h1 className="g-page__title">{t('lieux.titre')}</h1>
        <div className="g-actions">
          {tab === 'refuges' && (
            <Link to="nouveau?type=refuge" className="btn btn--primary">
              <span className="mdi mdi-home-plus-outline" aria-hidden /> {t('lieux.ajouter_refuge')}
            </Link>
          )}
          {tab === 'campings' && (
            <Link to="nouveau?type=camping" className="btn btn--primary">
              <span className="mdi mdi-tent" aria-hidden /> {t('lieux.ajouter_camping')}
            </Link>
          )}
        </div>
      </div>

      <div className="g-tabs" role="tablist">
        {tabs.map((x) => {
          const type = TAB_TYPE[x]
          return (
            <button key={x} type="button" role="tab" aria-selected={tab === x} className={`g-tab${tab === x ? ' g-tab--active' : ''}`} onClick={() => setTab(x)}>
              {t(`lieux.onglet_${x}`)}
              {type && <span className="g-chip__count">{counts[type] ?? 0}</span>}
            </button>
          )
        })}
      </div>

      {tab === 'tarification' ? (
        <section className="g-card g-embed">
          <OrganizationSettings organizationId={orgId} hiddenTabs={ALL_BUT_PRICING} />
        </section>
      ) : tab === 'emplacements' ? (
        <PitchesPanel orgId={orgId} />
      ) : (
      <>
      <div className="g-toolbar">
        <input
          className="g-input g-toolbar__search"
          type="search"
          placeholder={t('lieux.rechercher')}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {(error || loadError) && <p className="g-alert g-alert--error">{error ?? loadError?.message}</p>}
      {isLoading && <p className="g-muted">{t('commun.chargement')}</p>}
      {!isLoading && visible.length === 0 && (
        <div className="g-empty">
          <span className="mdi mdi-home-search-outline g-empty__icon" aria-hidden />
          <p>{q.trim() && counts[listType ?? ''] ? t('lieux.aucun_resultat') : t(`lieux.aucun_${tab}`)}</p>
        </div>
      )}

      <ul className="g-cards">
        {visible.map((l) => {
          const key = `${l.type}-${l.id}`
          const editable = (EDITABLE_TYPES as readonly string[]).includes(l.type)
          return (
            <li key={key} className="g-place">
              <div className="g-place__head">
                <TypeIcon type={l.type} />
                <div className="g-place__title">
                  {editable ? (
                    <Link to={editLocationPath(orgId, l.type, l.id)} className="g-place__name">
                      {l.name}
                    </Link>
                  ) : (
                    <span className="g-place__name">{l.name}</span>
                  )}
                  <span className="g-muted">
                    {t(`types.${l.type}`, { defaultValue: l.type })} · {t('lieux.modifie_le', { date: formatDate(l.updated_at) })}
                  </span>
                </div>
                {l.status !== 'active' && <StatusPill status={l.status} />}
              </div>
              <dl className="g-place__stats">
                <div title={t('stats.vues_30j')}>
                  <dt className="mdi mdi-eye-outline" aria-label={t('stats.vues_30j')} />
                  <dd>{formatNumber(views.get(key))}</dd>
                </div>
                <div title={t('stats.jaime')}>
                  <dt className="mdi mdi-heart-outline" aria-label={t('stats.jaime')} />
                  <dd>{formatNumber(l.like_count)}</dd>
                </div>
                <div title={t('stats.a_visiter')}>
                  <dt className="mdi mdi-bookmark-outline" aria-label={t('stats.a_visiter')} />
                  <dd>{formatNumber(l.to_visit_count)}</dd>
                </div>
                <div title={t('stats.commentaires')}>
                  <dt className="mdi mdi-comment-text-outline" aria-label={t('stats.commentaires')} />
                  <dd>{formatNumber(l.comment_count)}</dd>
                </div>
                {l.rating_count ? (
                  <div title={t('stats.note')}>
                    <dt className="mdi mdi-star-outline" aria-label={t('stats.note')} />
                    <dd>{(l.rating_avg ?? 0).toFixed(1)}</dd>
                  </div>
                ) : null}
              </dl>
              <div className="g-place__actions">
                {editable && (
                  <Link to={editLocationPath(orgId, l.type, l.id)} className="btn btn--sm btn--primary-soft">
                    <span className="mdi mdi-pencil-outline" aria-hidden /> {t('commun.modifier')}
                  </Link>
                )}
                <a href={publicLocationUrl(l)} target="_blank" rel="noreferrer" className="btn btn--sm">
                  <span className="mdi mdi-open-in-new" aria-hidden /> {t('commun.voir')}
                </a>
                {editable && (
                  <button
                    type="button"
                    className="btn btn--sm"
                    disabled={busyKey === key}
                    onClick={() => run(key, () => duplicateOrganizationLocation(orgId, l.id, { locationType: l.type }))}
                  >
                    <span className="mdi mdi-content-copy" aria-hidden /> {t('commun.dupliquer')}
                  </button>
                )}
                {editable && (
                  <button
                    type="button"
                    className="btn btn--sm g-danger"
                    disabled={busyKey === key}
                    onClick={() => {
                      if (window.confirm(t('lieux.confirmer_suppression', { name: l.name }))) {
                        void run(key, () => deleteOrganizationLocation(orgId, l.id, { locationType: l.type }))
                      }
                    }}
                  >
                    <span className="mdi mdi-trash-can-outline" aria-hidden /> {t('commun.supprimer')}
                  </button>
                )}
              </div>
            </li>
          )
        })}
      </ul>
      </>
      )}
    </div>
  )
}
